import { Component, ChangeDetectionStrategy, ChangeDetectorRef, Input, Output, EventEmitter, OnInit, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DateTime as LuxonDateTime } from 'luxon';

interface DateItem {
  date: string;
  month: string;
  day: number;
  weekday: string;
}

@Component({
  selector: 'tidy-date-scroller',
  templateUrl: './tidy-date-scroller.component.html',
  styleUrls: ['./tidy-date-scroller.component.scss'],
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TidyDateScrollerComponent implements OnInit, AfterViewInit {
  @Input() rangeStart: string | null = null;
  @Input() rangeEnd: string | null = null;
  @Input() daysToShow = 30;
  @Output() rangeChanged = new EventEmitter<{ start: string; end: string | null }>();

  @ViewChild('track', { static: false }) trackEl: ElementRef;

  dates: DateItem[] = [];
  showLeftArrow = false;
  showRightArrow = true;

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit() {
    const today = LuxonDateTime.now();
    for (let i = 0; i < this.daysToShow; i++) {
      const d = today.plus({ days: i });
      this.dates.push({
        date: d.toISODate(),
        month: d.toFormat('MMM').toUpperCase(),
        day: d.day,
        weekday: d.toFormat('ccc').toUpperCase()
      });
    }
  }

  ngAfterViewInit() {
    this.updateArrows();
  }

  selectDate(item: DateItem) {
    if (!this.rangeStart) {
      this.rangeStart = item.date;
      this.rangeEnd = null;
    } else if (!this.rangeEnd) {
      if (item.date === this.rangeStart) {
        this.rangeStart = null;
        this.rangeEnd = null;
      } else if (item.date < this.rangeStart) {
        this.rangeEnd = this.rangeStart;
        this.rangeStart = item.date;
      } else {
        this.rangeEnd = item.date;
      }
    } else {
      if (item.date >= this.rangeStart && item.date <= this.rangeEnd) {
        this.rangeStart = item.date;
        this.rangeEnd = null;
      } else if (item.date < this.rangeStart) {
        this.rangeStart = item.date;
      } else {
        this.rangeEnd = item.date;
      }
    }
    this.rangeChanged.emit({ start: this.rangeStart, end: this.rangeEnd });
    this.cdr.markForCheck();
  }

  isInRange(date: string): boolean {
    if (!this.rangeStart) {return false;}
    if (!this.rangeEnd) {return date === this.rangeStart;}
    return date >= this.rangeStart && date <= this.rangeEnd;
  }

  scrollRight = () => {
    const el = this.trackEl?.nativeElement;
    if (el) {el.scrollBy({ left: el.clientWidth, behavior: 'smooth' });}
  };

  scrollLeft = () => {
    const el = this.trackEl?.nativeElement;
    if (el) {el.scrollBy({ left: -el.clientWidth, behavior: 'smooth' });}
  };

  onScroll = () => {
    this.updateArrows();
  };

  private updateArrows() {
    const el = this.trackEl?.nativeElement;
    if (!el) {return;}
    this.showLeftArrow = el.scrollLeft > 10;
    this.showRightArrow = el.scrollLeft < el.scrollWidth - el.clientWidth - 10;
  }

  trackByDate(_index: number, item: DateItem): string {
    return item.date;
  }
}
